import React, { useEffect, useState } from "react";
import { projectsAPI, stageAssignmentsAPI, tenantAPI } from "../../api/client";
import { todayString } from "../../utils/dateUtils";
import { PERMISSIONS } from "../../constants/permissions";
import useAuth from "../../hooks/useAuth";
import EligibleUserSelect from "./EligibleUserSelect";
import ProjectDocumentsField from "./ProjectDocumentsField";
import "./Projects.css";

export default function EditProjectModal({
  open,
  project,
  onClose,
  onUpdated,
}) {
  const { user } = useAuth();
  const canEditCore =
    !!user?.isAdmin || !!user?.permissions?.includes(PERMISSIONS.PROJECT_CREATE);
  const canMarkUrgent =
    !!user?.isAdmin ||
    !!user?.permissions?.includes(PERMISSIONS.PROJECT_ASSIGN_USERS);
  const [form, setForm] = useState({
    name: project?.name || "",
    description: project?.description || "",
    durationValue: project?.durationValue || "",
    durationUnit: project?.durationUnit || "DAYS",
    isUrgent: !!project?.isUrgent,
  });
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");
  const [fullProject, setFullProject] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [customFieldsConfig, setCustomFieldsConfig] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [documentsFieldConfig, setDocumentsFieldConfig] = useState({});
  const documentsVisible = documentsFieldConfig.visible !== false;
  const documentsRequired = documentsVisible && documentsFieldConfig.required === true;

  useEffect(() => {
    if (!open) return;
    tenantAPI.getTenant().then((resp) => {
      const fields = Array.isArray(resp?.data?.projectFormSchema?.fields)
        ? resp.data.projectFormSchema.fields
        : [];
      setCustomFieldsConfig(fields);
      setDocumentsFieldConfig(
        resp?.data?.projectFormSchema?.systemFieldConfig?.documents || {},
      );
      setCustomFieldValues(
        fields.reduce((acc, field) => {
          acc[field.key] = project?.customFields?.[field.key] ?? "";
          return acc;
        }, {}),
      );
    }).catch(() => {});
  }, [open, project?.id]);

  useEffect(() => {
    const normalizedProjectImages = Array.isArray(project?.imageUrl)
      ? project.imageUrl
      : project?.imageUrl
        ? [project.imageUrl]
        : [];
    setForm({
      name: project?.name || "",
      description: project?.description || "",
      durationValue: project?.durationValue || "",
      durationUnit: project?.durationUnit || "DAYS",
      isUrgent: !!project?.isUrgent,
      imageUrls: normalizedProjectImages.filter((u) => !u.toLowerCase().endsWith(".stl")),
      stlUrls: normalizedProjectImages.filter((u) => u.toLowerCase().endsWith(".stl")),
      tempViewUrls: normalizedProjectImages
        .filter((u) => !u.toLowerCase().endsWith(".stl"))
        .map((imageUrl) => `${process.env.REACT_APP_API_UPLOAD_URL}${imageUrl}`),
      imageFiles: [],
      stlFiles: [],
      existingDocuments: Array.isArray(project?.documents)
        ? project.documents
        : [],
      documentFiles: [],
    });
  }, [project]);

  useEffect(() => {
    if (!open || !project?.id) return;
    const load = async () => {
      try {
        const resp = await projectsAPI.getById(project.id);
        setFullProject(resp.data);
        setForm((prev) => ({
          ...prev,
          existingDocuments: Array.isArray(resp.data?.documents)
            ? resp.data.documents
            : [],
        }));
        const defaultStageId =
          resp.data?.currentStageIds?.[0] ||
          resp.data?.currentStageId ||
          resp.data?.workflow?.definition?.stages?.[0]?.id ||
          "";
        setSelectedStageId(defaultStageId);
      } catch (err) {
        setError("Failed to load project assignment details");
      }
    };
    load();
  }, [open, project?.id]);

  if (!open || !project) return null;

  const handleImage = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    const nextPreviewUrls = nextFiles.map((file) => URL.createObjectURL(file));
    setForm((prev) => {
      (prev.tempViewUrls || []).forEach((url) => {
        if (url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
      return {
        ...prev,
        imageFiles: nextFiles,
        tempViewUrls: nextPreviewUrls,
      };
    });
  };

  const handleModalClose = () => {
    (form.tempViewUrls || []).forEach((url) => {
      if (url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      let resp;
      if (
        canEditCore &&
        documentsRequired &&
        (form.existingDocuments || []).length +
          (form.documentFiles || []).length ===
          0
      ) {
        setError("At least one project document is required.");
        return;
      }
      if (canEditCore) {
        const data = new FormData();
        data.append("name", form.name);
        data.append("description", form.description);
        data.append("durationValue", form.durationValue);
        data.append("durationUnit", form.durationUnit);
        data.append("isUrgent", String(!!form.isUrgent));
        data.append("customFields", JSON.stringify(customFieldValues || {}));
        const existingUrls = [
          ...(form.imageUrls || []),
          ...(form.stlUrls || []),
        ];
        if (existingUrls.length > 0) {
          data.append("imageUrls", JSON.stringify(existingUrls));
        }
        (form.imageFiles || []).forEach((file) => data.append("images", file));
        (form.stlFiles || []).forEach((file) => data.append("images", file));
        // Only send the keep-list once the saved documents have loaded, so a
        // slow fetch can't wipe them.
        if (fullProject) {
          data.append(
            "existingDocuments",
            JSON.stringify((form.existingDocuments || []).map((d) => d.path)),
          );
        }
        (form.documentFiles || []).forEach((file) =>
          data.append("documents", file),
        );
        resp = await projectsAPI.update(project.id, data);
      } else if (canMarkUrgent) {
        resp = await projectsAPI.setUrgency(project.id, !!form.isUrgent);
      } else {
        throw new Error("You don't have permission to update this project");
      }

      onUpdated(resp.data);
      handleModalClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const reassign = async () => {
    if (!selectedStageId || !selectedAssigneeId || !fullProject) return;
    setAssigning(true);
    setError("");
    try {
      const existing = [...(fullProject.stageAssignments || [])]
        .filter((item) => item.stageId === selectedStageId)
        .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt))[0];
      if (existing) {
        await stageAssignmentsAPI.reassign(project.id, existing.id, {
          assigneeId: selectedAssigneeId,
        });
      } else {
        await stageAssignmentsAPI.create(project.id, {
          stageId: selectedStageId,
          assigneeId: selectedAssigneeId,
          startDate: todayString(),
          scheduledStartTime: "09:00",
          isOvertime: false,
        });
      }
      const refreshed = await projectsAPI.getById(project.id);
      setFullProject(refreshed.data);
      setSelectedAssigneeId("");
      onUpdated(refreshed.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign user");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <form className="modal-content improved-modal" onSubmit={submit}>
        <div className="modal-header">
          <h2>Edit Project</h2>
          <button type="button" className="close-btn" onClick={handleModalClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="edit-project-name">Project Name *</label>
              <input
                id="edit-project-name"
                required
                disabled={!canEditCore}
                value={form.name}
                placeholder="Enter project name"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-project-description">Description</label>
              <textarea
                id="edit-project-description"
                rows={3}
                disabled={!canEditCore}
                value={form.description}
                placeholder="Enter project description"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-section two-col">
            <div className="form-group">
              <label htmlFor="edit-project-duration">Duration *</label>
              <input
                id="edit-project-duration"
                required
                type="number"
                min="1"
                disabled={!canEditCore}
                value={form.durationValue}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    durationValue: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-project-duration-unit">Unit</label>
              <select
                id="edit-project-duration-unit"
                disabled={!canEditCore}
                value={form.durationUnit}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, durationUnit: e.target.value }))
                }
              >
                <option value="HOURS">Hours</option>
                <option value="DAYS">Days</option>
              </select>
            </div>
          </div>

          {canMarkUrgent && (
            <div className="form-section">
              <div
                className="form-group"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <input
                  id="edit-project-is-urgent"
                  type="checkbox"
                  checked={!!form.isUrgent}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, isUrgent: e.target.checked }))
                  }
                  style={{ width: "auto" }}
                />
                <label htmlFor="edit-project-is-urgent" style={{ marginBottom: 0 }}>
                  Mark this project as urgent
                </label>
              </div>
              {!canEditCore && (
                <small className="small-muted">
                  You can update urgency here. Other project fields are read-only
                  for your role.
                </small>
              )}
            </div>
          )}

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="edit-project-images">Project Images</label>
              <input
                id="edit-project-images"
                type="file"
                name="images"
                accept="image/*"
                multiple
                disabled={!canEditCore}
                onChange={(e) => handleImage(e.target.files)}
              />
              <small>{form.imageFiles?.length || 0} image(s) selected</small>
            </div>
            {form.tempViewUrls?.length > 0 && (
              <div className="project-image-preview-list">
                {form.tempViewUrls.map((imageUrl, index) => (
                  <div key={`${imageUrl}-${index}`} style={{ position: "relative", display: "inline-block" }}>
                    <img
                      className="project-image-preview"
                      src={imageUrl}
                      alt={`${project.name}-${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          imageUrls: (prev.imageUrls || []).filter((_, i) => i !== index),
                          tempViewUrls: (prev.tempViewUrls || []).filter((_, i) => i !== index),
                        }))
                      }
                      style={{
                        position: "absolute", top: 2, right: 2,
                        background: "rgba(0,0,0,0.55)", border: "none",
                        color: "#fff", borderRadius: 4, cursor: "pointer",
                        fontSize: 13, lineHeight: 1, padding: "2px 5px",
                      }}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-group">
              <label>3D Model Files (.stl)</label>
              <input
                type="file"
                accept=".stl"
                multiple
                disabled={!canEditCore}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    stlFiles: Array.from(e.target.files || []),
                  }))
                }
              />
              <small>{form.stlFiles?.length || 0} new file(s) selected</small>
            </div>
            {form.stlUrls?.length > 0 && (
              <div>
                <small className="small-muted">Existing STL files:</small>
                {form.stlUrls.map((url, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <span className="small-muted">{url.split("/").pop()}</span>
                    <button
                      type="button"
                      className="btn-danger btn-sm"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          stlUrls: (prev.stlUrls || []).filter((_, j) => j !== i),
                        }))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {form.stlFiles?.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {form.stlFiles.map((file, i) => (
                  <div key={i} className="small-muted">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {documentsVisible && (
            <ProjectDocumentsField
              existing={form.existingDocuments || []}
              files={form.documentFiles || []}
              disabled={!canEditCore}
              required={documentsRequired}
              onExistingChange={(existingDocuments) =>
                setForm((prev) => ({ ...prev, existingDocuments }))
              }
              onFilesChange={(documentFiles) =>
                setForm((prev) => ({ ...prev, documentFiles }))
              }
            />
          )}

          {fullProject && (
            <div className="form-section">
              <div className="panel">
                <h4 style={{ marginTop: 0 }}>Reassign Stage</h4>
                <div className="form-group">
                  <label htmlFor="edit-project-stage">Stage</label>
                  <select
                    id="edit-project-stage"
                    value={selectedStageId}
                    onChange={(e) => setSelectedStageId(e.target.value)}
                  >
                    <option value="">Select stage...</option>
                    {(fullProject.workflow?.definition?.stages || []).map(
                      (stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assign To</label>
                  <EligibleUserSelect
                    projectId={project.id}
                    stageId={selectedStageId}
                    value={selectedAssigneeId}
                    onChange={setSelectedAssigneeId}
                    disabled={assigning}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={!selectedStageId || !selectedAssigneeId || assigning}
                    onClick={reassign}
                  >
                    {assigning ? "Assigning..." : "Assign / Reassign"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {customFieldsConfig.length > 0 && (
            <div className="form-section">
              <h4 style={{ marginBottom: 6 }}>Additional Fields</h4>
              <div className="modal-grid two-col">
                {customFieldsConfig.map((field) => (
                  <div key={field.key}>
                    <label>
                      {field.label}
                      {field.required ? " *" : ""}
                    </label>
                    {field.type === "select" ? (
                      <select
                        disabled={!canEditCore}
                        value={customFieldValues[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        required={field.required}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        disabled={!canEditCore}
                        value={customFieldValues[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        required={field.required}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving
              ? "Saving..."
              : canEditCore
                ? "Update Project"
                : "Save Urgency"}
          </button>
        </div>
      </form>
    </div>
  );
}
